import {Component, inject, OnDestroy, OnInit, HostListener} from '@angular/core';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

import {Globals} from '../../../core/services/globals';
import {CollapseModule} from 'ngx-bootstrap/collapse';
import {CommonModule} from '@angular/common';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {NgbDropdown} from '@ng-bootstrap/ng-bootstrap';
import {environment} from "../../../../environments/environment";

@Component({
  standalone: true,
  imports: [CommonModule, RouterModule, CollapseModule, NgbModule, NgbDropdown],
  selector: 'my-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.css'],
})
export class NavigationComponent implements OnInit, OnDestroy {
  public isCollapsed = true;
  public isLinksEnabled = false;
  public isPeelHaltonSite = false; // This can be set based on the current route or environment
  destroyed = new Subject<void>();
  currentScreenSize: string | undefined;
  public isMobileView = window.innerWidth <= 822;
  dropdownOpen: number | null = null;

  // Menu items (example)
  menuItems = [
    {name: 'Home', link: '/', subItems: [], isActive: false},
    {
      name: 'Featured Listings',
      link: '/search',
      subItems: [
        {name: 'Muskoka', link: '/search'},
        {name: 'Toronto', link: '/search/toronto'},
        {name: 'Oakville', link: '/search/oakville'},
        {name: 'Burlington', link: '/search/burlington'},
        {name: 'Mississauga', link: '/search/mississauga'}
      ],
      isActive: false
    },
    {name: 'Recent Sales', link: '/search/sold', subItems: [], isActive: false},
    {name: 'Rentals', link: '/search/rentals', subItems: [], isActive: false},
    {
      name: 'Associates',
      link: '#',
      subItems: [
        {name: 'North', link: 'https://www.caymanmarshall.com/associates-north'},
        {name: 'South', link: 'https://www.caymanmarshall.com/associates-south'},
      ],
      isActive: false
    },
    {name: 'Buying', link: 'https://www.caymanmarshall.com/buying', subItems: [], isActive: false},
    {name: 'Selling', link: 'https://www.caymanmarshall.com/selling', subItems: [], isActive: false},
    {
      name: 'Media',
      link: '#',
      subItems: [
        {name: 'Magazines', link: 'https://www.caymanmarshall.com/magazines'},
      ],
      isActive: false
    },
    {name: 'About Us', link: 'https://www.caymanmarshall.com/company', subItems: [], isActive: false},
  ];

  // Create a map to display breakpoint names for demonstration purposes.
  displayNameMap = new Map([
    [Breakpoints.Handset, 'Handset'],
    [Breakpoints.Tablet, 'Tablet'],
    [Breakpoints.Web, 'Web'],
  ]);

  constructor(
    public router: Router,
    private http: HttpClient,
    private responsive: BreakpointObserver,
    public globals: Globals,
  ) {
    inject(BreakpointObserver)
      .observe([Breakpoints.Handset, Breakpoints.Tablet, Breakpoints.Web])
      .pipe(takeUntil(this.destroyed))
      .subscribe((result) => {
        for (const query of Object.keys(result.breakpoints)) {
          if (result.breakpoints[query]) {
            this.currentScreenSize =
              this.displayNameMap.get(query) ?? 'Unknown';
          }
        }
      });
    if (globals.linksEnabledBln) {
      this.isLinksEnabled = true;
    }
    // Check if the current site is PeelHalton
    this.isPeelHaltonSite = environment.hostname.includes('oakville');
  }

  ngOnInit() {
    // Wait for the router to initialize to avoid flashing on load
    setTimeout(() => {
      this.updateActiveLink();
    }, 100);

    // Subscribe to router events to detect navigation changes
    this.router.events.pipe(
      takeUntil(this.destroyed)
    ).subscribe(event => {
      // Check if it's a NavigationEnd event (navigation completed successfully)
      if (event instanceof NavigationEnd) {
        const previousUrl = this.router.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString();
        const currentUrl = event.url;

        // If navigating away from a URL with encid parameter
        if (previousUrl?.includes('?encid=') && !currentUrl.includes('?encid=')) {
          // Clear the savedSearchIDStr when navigating away from encid URL
          this.globals.savedSearchIDStr = '';
        }
      }
    });

  }

  updateActiveLink() {
    const activeLink = this.router.url;

    // Check if URL contains encid parameter
    if (activeLink.includes('?encid=')) {
      // If encid is present, ensure no menu items are active
      this.menuItems.forEach(item => {
        item.isActive = false;
      });
      return; // Early return to avoid setting any items as active
    }

    // Special case for the base URL ('/')
    if (activeLink === '/') {
      // Only set Featured Listings as active
      const featuredListingsItem = this.menuItems.find(item => item.name === 'Featured Listings');
      if (featuredListingsItem) {
        featuredListingsItem.isActive = true;
      }
      return;
    }

    // First, reset all active states
    this.menuItems.forEach(item => {
      item.isActive = false;
    });

    // Check if we're on a sold property page (list or detail)
    const isSoldPath = activeLink === '/search/sold' || activeLink.startsWith('/search/sold/');

    // Check if we're on a rental property page (list or detail)
    const isRentalsPath = activeLink === '/search/rentals' || activeLink.startsWith('/search/rentals/');

    // Handle special paths first
    if (isSoldPath) {
      const soldItem = this.menuItems.find(item => item.link === '/search/sold');
      if (soldItem) {
        soldItem.isActive = true;
      }
      return; // Early return to avoid activating other items
    }

    if (isRentalsPath) {
      const rentalsItem = this.menuItems.find(item => item.link === '/search/rentals');
      if (rentalsItem) {
        rentalsItem.isActive = true;
      }
      return; // Early return to avoid activating other items
    }

    // Now handle the regular menu items
    this.menuItems.forEach(item => {
      // Special case for Featured Listings
      if (item.link === '/search') {
        // Active if on specific Featured Listings paths and not on special sections
        item.isActive = activeLink === '/search' ||
          activeLink === '/' ||
          activeLink === '/search/toronto' ||
          activeLink === '/search/oakville' ||
          activeLink === '/search/burlington' ||
          activeLink === '/search/mississauga' ||
          (activeLink.startsWith('/search/') &&
            !isSoldPath &&
            !isRentalsPath &&
            !activeLink.startsWith('/search/buying/') &&
            !activeLink.startsWith('/search/selling/') &&
            !activeLink.startsWith('/search/about/'));
      }
      // For home, only exact match
      else if (item.link === '/') {
        item.isActive = activeLink === '/';
      }
      // For Recent Sales, already handled above
      else if (item.link === '/search/sold') {
        // This is redundant but keeping for clarity
        item.isActive = isSoldPath;
      }
      // For Rentals, already handled above
      else if (item.link === '/search/rentals') {
        // This is redundant but keeping for clarity
        item.isActive = isRentalsPath;
      }
      // For all other menu items
      else {
        // Don't highlight other items when on special paths
        if (isSoldPath || isRentalsPath) {
          item.isActive = false;
        } else {
          item.isActive = activeLink === item.link ||
            (activeLink.startsWith(item.link + '/') &&
              // Don't match special sections
              !(item.link === '/search' && (isSoldPath || isRentalsPath)));
        }
      }

      // Check sub-items (but don't do this for special sections)
      if (item.subItems.length > 0 && item.link !== '/search/sold' && item.link !== '/search/rentals') {
        // Skip checking subitems if we're on a special path
        if (!isSoldPath && !isRentalsPath) {
          const hasActiveSubItem = item.subItems.some(subItem =>
            activeLink === subItem.link ||
            activeLink.startsWith(subItem.link + '/')
          );

          if (hasActiveSubItem) {
            item.isActive = true;
          }
        }
      }
    });
  }

  /**
   * Navigate to the specified link, forcing a reload if we're coming from an encid URL
   * @param link The route to navigate to
   * @param event Optional event object
   */
  navigateToLink(link: string, event?: Event): void {
    // If this was triggered by a keyboard event, only proceed if it was Enter or Space
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      // Prevent default for space key to avoid page scrolling
      if (event.key === ' ') {
        event.preventDefault();
      }
    }

    // Check if it's an external link (starts with http:// or https://)
    if (link.startsWith('http://') || link.startsWith('https://')) {
      // For external links, navigate directly in the same window
      window.location.href = link;
      return;
    }

    // If it's just a placeholder link (#)
    if (link === '#') {
      return; // Do nothing, just prevent default
    }


    const currentUrl = this.router.url;

    // Check if we're currently on a page with encid parameter
    if (currentUrl.includes('?encid=')) {
      // Clear saved search ID
      this.globals.savedSearchIDStr = '';

      // Force reload by navigating to the same URL but with skipLocationChange
      this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
        this.router.navigate([link]);
      });
    } else {
      // If we're navigating to the same route without encid, force reload
      if (currentUrl.split('?')[0] === link) {
        this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
          this.router.navigate([link]);
        });
      } else {
        // Standard navigation for different routes
        this.router.navigate([link]);
      }
    }
  }


  redirect(idStr: string) {
    this.router.navigate(['./' + idStr]);
  }

  // Open the dropdown when mouse enters
  openDropdown(index: number) {
    this.dropdownOpen = index;
  }

  // Close the dropdown when mouse leaves
  closeDropdown() {
    this.dropdownOpen = null;
  }

  // Toggle the collapsed state of the navbar
  toggleCollapse() {
    console.log('toggle collapse');
    this.isCollapsed = !this.isCollapsed;
  }

  // Collapse the menu when a link is clicked
  collapseMenu() {
    if (this.isMobileView) {
      this.isCollapsed = true;
      this.globals.savedSearchIDStr = ''; // Example action when collapsing
    }
  }

  // Check if the item is active
  isActive(item: any): boolean {
    return item.isActive;
  }

  // Listen for window resize events and update mobile view detection
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isMobileView = event.target.innerWidth <= 822;
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
